import {Fragment, useState} from 'react';
// @ts-expect-error @headlessui/react incompatibility with node16 resolution
import {Dialog, Transition} from '@headlessui/react';
import {useProductOptions} from '@shopify/hydrogen';
import clsx from 'clsx';
import 'react-calendar/dist/Calendar.css';
import './style.css';

import {
  IShopifyProductVariantTimeslotsInput,
  IVariantTimeslot,
  ShopifyProductVariantTimeslotsDocument,
} from '~/lib/types/graphql/api';
import {useAppContext, PostalSearchForm} from '~/components';
import {DISPLAY_TIMEZONE, queryBe} from '~/utils';
import {useQuery} from 'react-query';
import {CartLineInput} from '@shopify/hydrogen/storefront-api-types';
import {v4} from 'uuid';
import {formatInTimeZone} from 'date-fns-tz';
import {ProductSelectTimeslot} from '../booking_steps/ProductSelectTimeslot.client';
import {useMobile} from '~/hooks';

export interface ProductBookingModalConfirmation {
  timeslot: IVariantTimeslot;
  productLine: CartLineInput;
  feeLine?: CartLineInput;
}

interface Props {
  isOpen: boolean;
  isGift: boolean;
  onClose: () => void;
  onConfirmTimeslot: (confirmation: ProductBookingModalConfirmation) => void;
  confirmButtonTitle: string;
}

export function ProductBookingModal({
  isOpen,
  isGift,
  onClose,
  onConfirmTimeslot,
  confirmButtonTitle,
}: Props) {
  const {selectedVariant, selectedSellingPlan} = useProductOptions();

  const [selectedTimeslot, setSelectedTimeslot] = useState<IVariantTimeslot>();
  const [date, setDate] = useState<Date>(new Date());
  const isMobile = useMobile();

  const {searchPostcode} = useAppContext();

  const {
    data: timeslots,
    isLoading: isLoadingTimeslots,
    error: timeslotsError,
    // Linter doesn't understand that this is react-query useQuery, not server side one
    // eslint-disable-next-line hydrogen/client-component-banned-hooks
  } = useQuery(
    ['product-variant-timeslots', selectedVariant?.id, date, searchPostcode],
    async () => {
      const {data, errors} = await queryBe<
        'shopifyProductVariantTimeslots',
        (input: IShopifyProductVariantTimeslotsInput) => IVariantTimeslot[]
      >({
        query: ShopifyProductVariantTimeslotsDocument,
        variables: {
          input: {
            id: selectedVariant?.id ?? '',
            date: {
              year: date.getFullYear(),
              month: date.getMonth(),
              day: date.getDate(),
            },
            postcode: searchPostcode,
          },
        },
      });
      if (errors) {
        throw new Error(errors.map((e) => e.message).join(', '));
      }

      return data.shopifyProductVariantTimeslots;
    },
    {
      retry: false,
      enabled: searchPostcode.length === 6,
    },
  );

  if (!selectedVariant) {
    return <></>;
  }

  const AddToCartBtn = ({forMobile = false}: {forMobile?: boolean}) => {
    return (
      <button
        className={clsx(
          'w-full border-[0.5px] border-acai-900 bg-acai-900 py-4 text-sm font-normal text-white disabled:cursor-not-allowed disabled:bg-white disabled:text-acai-900 md:block',
          forMobile
            ? 'w-3/4 rounded md:hidden'
            : 'hidden w-[300px] rounded-full',
        )}
        onClick={() => {
          //Should never happen, but just to be safe
          if (!selectedTimeslot) {
            return;
          }
          //Generate an id for matching surge fee lines
          const tfrLineId = v4();
          onConfirmTimeslot({
            timeslot: selectedTimeslot,
            productLine: {
              merchandiseId: selectedVariant.id!,
              sellingPlanId: selectedSellingPlan?.id,
              attributes: [
                {
                  key: 'Date & Time',
                  value: formatInTimeZone(
                    new Date(selectedTimeslot.dateTime),
                    DISPLAY_TIMEZONE,
                    'MMM d yyy, hh:mm b',
                  ),
                },
                {
                  key: '_tfr_line_id',
                  value: tfrLineId,
                },
                {
                  key: '_start_time',
                  value: new Date(selectedTimeslot.dateTime).toISOString(),
                },
                {
                  key: '_timezone',
                  value: 'Singapore',
                },
                {
                  key: '_postcode',
                  value: searchPostcode,
                },
                {
                  key: '_tfr_booking',
                  value: 'true',
                },
                {
                  key: '_tfr_is_gift',
                  value: isGift ? 'true' : 'false',
                },
                {
                  key: '_tfr_consultant_ids',
                  value: JSON.stringify(selectedTimeslot.consultantIds),
                },
                {
                  key: '_tfr_booking_creator_role',
                  value: 'user',
                },
              ],
            },
            //We only do surge fees for one-time bookings, ie no selling plan selected
            feeLine:
              selectedTimeslot.surgeFee && !selectedSellingPlan
                ? {
                    merchandiseId: selectedTimeslot.surgeFee.variantShopifyId,
                    attributes: [{key: '_for_tfr_line_id', value: tfrLineId}],
                  }
                : undefined,
          });
          onClose();
        }}
        disabled={!selectedTimeslot}
      >
        {confirmButtonTitle}
      </button>
    );
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="h-full w-full text-center md:flex md:items-center md:justify-center md:p-4 md:pt-[80px]">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={clsx(
                  'booking-steps-wrapper p-8',
                  !isMobile && 'max-h-[800px] max-w-[450px]',
                )}
              >
                <PostalSearchForm
                  showEmptyFieldError
                  containerClassName="mb-6 md:mb-7"
                  hasResults={
                    !isLoadingTimeslots && timeslots && timeslots?.length > 0
                  }
                  postalCodeFetchError={
                    timeslotsError
                      ? 'There was an error getting timeslots. Please try again later.'
                      : undefined
                  }
                />

                <div className="mb-4 flex-grow overflow-auto">
                  <ProductSelectTimeslot
                    date={date}
                    productTitle={selectedVariant?.product?.title || ''}
                    onSetDate={(pDate: Date) => {
                      setDate(pDate);
                    }}
                    timeslots={timeslots}
                    timeslotsError={timeslotsError}
                    selectedVariant={selectedVariant}
                    selectedTimeslot={selectedTimeslot}
                    isLoadingTimeslots={isLoadingTimeslots}
                    selectedSellingPlan={selectedSellingPlan}
                    onSetSelectedTimeslot={(v) => {
                      setSelectedTimeslot(v);
                    }}
                  />
                </div>

                <AddToCartBtn />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
